/**
 * DEMO-ONLY: discovery prototype HTTP rewrites (addresses, catalog, pricing, purchase rules).
 */
import { HttpErrorResponse, HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';

import {
  addAccessRequest,
  evaluatePurchaseRules,
  getCatalogForActiveCustomer,
  getDiscoveryUser,
  getNegotiatedPrice,
  getPendingApprovalOrder,
  getPresidioDiscoveryState,
  isAddressHidden,
  isSkuAllowedForActiveCustomer,
  listPendingApprovalOrdersForView,
  recordPendingApprovalOrder,
  updatePendingApprovalOrderStatus,
  type PresidioDiscoveryPendingApprovalOrder,
  type PresidioDiscoveryPendingApprovalStatus,
} from './presidio-mock-discovery';
import {
  getPresidioMockActiveCustomerNo,
  getPresidioMockActiveLogin,
  isPresidioMockWarehouseLogin,
  PRESIDIO_MOCK_BASKET_SELECTION_KEYS,
  PRESIDIO_MOCK_CSR_IMPERSONATION_TARGETS,
  resetPresidioMockCartSessionOnUserChange,
} from './presidio-mock-session';
import {
  getPresidioWarehouseHeldItem,
  isPresidioWarehouseHeldSku,
  warehouseHeldUnitPriceOverride,
} from './presidio-mock-warehouse-held-inventory';
import { lookupPresidioUnspsc, unspscMatchesSearchToken } from './presidio-mock-product-unspsc';

const MY_WAREHOUSE_CATEGORY_IDS = new Set(['WH', 'my-warehouse']);

/** Facet id for warehouse-account “In My Warehouse” filter (MVP 2.6.3 / FR5.3.6). */
export const PRESIDIO_IN_MY_WAREHOUSE_FILTER_ID = 'InMyWarehouse';

/** Selected value when the In My Warehouse checkbox is on. */
export const PRESIDIO_IN_MY_WAREHOUSE_FILTER_VALUE = 'true';

export function isInMyWarehouseFilterSelected(selected: Record<string, string[]> | undefined): boolean {
  const values = selected?.[PRESIDIO_IN_MY_WAREHOUSE_FILTER_ID] || selected?.['inmywarehouse'] || [];
  return values.some(v => {
    const decoded = decodeURIComponent(String(v || '')).trim().toLowerCase();
    return decoded === PRESIDIO_IN_MY_WAREHOUSE_FILTER_VALUE || decoded === 'in my warehouse' || decoded === 'yes';
  });
}

/** Keep only held warehouse SKUs (used when InMyWarehouse facet is selected). */
export function filterProductListElementsByInMyWarehouse<
  T extends { attributes?: Array<{ name?: string; value?: unknown }> },
>(elements: T[]): T[] {
  return elements.filter(el => isPresidioWarehouseHeldSku(extractProductListStubSku(el)));
}

function isMyWarehouseFilterBasePath(filterBasePath: string): boolean {
  return /(?:^|\/)categories\/(?:WH|my-warehouse)(?:\/|$)/i.test(filterBasePath);
}

function isMyWarehouseCategoryRef(refOrId: string | undefined): boolean {
  if (!refOrId) {
    return false;
  }
  const id = refOrId.includes('@') ? refOrId.split('@')[0] : refOrId;
  return MY_WAREHOUSE_CATEGORY_IDS.has(id) || id === 'my-warehouse';
}

/**
 * DEMO: My Warehouse (WH) is first in the mega-menu only for the warehouse account
 * (Casey Ware / PRESIDIO-WH-MOCK — mock login casey.ware@presidio-wh.com).
 * Non-warehouse customers (e.g. Jordan Hale / Acme) never see the category.
 */
export function filterMyWarehouseCategoryFromTree(body: unknown): unknown {
  if (!body || typeof body !== 'object' || isPresidioMockWarehouseLogin()) {
    return body;
  }
  const clone = JSON.parse(JSON.stringify(body)) as {
    elements?: Array<{ categoryRef?: string; categoryPath?: Array<{ id?: string }>; uri?: string }>;
  };
  if (!Array.isArray(clone.elements)) {
    return body;
  }
  clone.elements = clone.elements.filter(el => {
    const pathId = el.categoryPath?.[0]?.id;
    if (pathId && MY_WAREHOUSE_CATEGORY_IDS.has(pathId)) {
      return false;
    }
    if (isMyWarehouseCategoryRef(el.categoryRef)) {
      return false;
    }
    if (typeof el.uri === 'string' && /\/categories\/WH(?:\/|$|\?)/.test(el.uri)) {
      return false;
    }
    return true;
  });
  return clone;
}

/**
 * DEMO: map Request-access ERP numbers to the discovery account that owns approved
 * email domains (User Management). Acme demo ERPs share PRESIDIO-MOCK policy.
 */
export function resolveAccessRequestDomainCustomerNo(erpCustomerNo: string): string | undefined {
  const erp = String(erpCustomerNo || '')
    .trim()
    .toUpperCase();
  if (!erp || erp === 'INVALID' || erp === 'NOTFOUND' || erp === 'UNKNOWN' || erp === 'XXX') {
    return undefined;
  }
  if (erp === 'PCT-10042' || erp.includes('ADMIN') || erp.startsWith('ACME')) {
    return 'PRESIDIO-MOCK';
  }
  return undefined;
}

/** DEMO: ERP verify / access-request routing (FR 1.2.1 / 1.2.7). */
export function classifyPresidioAccessErp(erpCustomerNo: string): {
  found: boolean;
  hasAdministrators: boolean;
  hasAssignedCsr: boolean;
  contactSalesRep: boolean;
  companyName?: string;
  csrName?: string;
  /** Discovery customerNo used for approved email-domain checks when known. */
  domainCustomerNo?: string;
} {
  const erp = String(erpCustomerNo || '')
    .trim()
    .toUpperCase();
  if (!erp || erp === 'INVALID' || erp === 'NOTFOUND' || erp === 'UNKNOWN' || erp === 'XXX') {
    return {
      found: false,
      hasAdministrators: false,
      hasAssignedCsr: false,
      contactSalesRep: true,
    };
  }

  // Production assumption: every known ERP has an account admin and/or an assigned CSR.
  // Demo only models admin vs CSR routing — never "found but no CSR".
  const hasAdministrators = erp.includes('ADMIN') || erp === 'PCT-10042';
  const companyName =
    erp === 'PCT-10042' || erp.includes('ADMIN')
      ? 'Acme Corporation'
      : erp.startsWith('ACME')
        ? 'Acme Industries'
        : `Customer ${erp}`;

  return {
    found: true,
    hasAdministrators,
    hasAssignedCsr: true,
    contactSalesRep: false,
    companyName,
    csrName: 'Quinn Cole',
    domainCustomerNo: resolveAccessRequestDomainCustomerNo(erp),
  };
}

/** Clear checkout address selections after account switch (stale bill/ship prevention). */
export function clearPresidioDiscoveryCheckoutAddressSelections(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  for (const key of PRESIDIO_MOCK_BASKET_SELECTION_KEYS) {
    sessionStorage.removeItem(key);
  }
}

export function onPresidioDiscoveryAccountSwitch(): void {
  resetPresidioMockCartSessionOnUserChange();
  clearPresidioDiscoveryCheckoutAddressSelections();
}

/** Filter eligible / customer addresses by active-account visibility. */
export function filterAddressesForDiscoveryVisibility(
  addresses: Record<string, unknown>[]
): Record<string, unknown>[] {
  const customerNo = getPresidioMockActiveCustomerNo();
  return addresses.filter(addr => {
    const id = String(addr.id || '');
    if (!id) {
      return true;
    }
    return !isAddressHidden(customerNo, id);
  });
}

/** Sort recently-used first using discovery meta. */
export function sortAddressesRecentlyUsed(
  addresses: Record<string, unknown>[]
): Record<string, unknown>[] {
  const customerNo = getPresidioMockActiveCustomerNo();
  const meta = getPresidioDiscoveryState().addressMeta.filter(a => a.customerNo === customerNo);
  const rank = (id: string) => {
    const row = meta.find(a => a.id === `${customerNo}:${id}` || a.id.endsWith(`:${id}`));
    return row?.recentlyUsed ? 0 : 1;
  };
  return [...addresses].sort((a, b) => rank(String(a.id || '')) - rank(String(b.id || '')));
}

export function applyDiscoveryPriceToProductPayload(body: unknown, sku: string): unknown {
  if (!body || typeof body !== 'object') {
    return body;
  }
  // MVP 2.4.7 — never invent a negotiated price for price-on-request SKUs
  const asRecord = body as Record<string, unknown>;
  if (asRecord.priceOnRequest === true) {
    return body;
  }
  // MVP 2.6.3 — warehouse held SKUs display $0 for warehouse login (owned stock)
  const heldOverride = warehouseHeldUnitPriceOverride(sku);
  const negotiated = getNegotiatedPrice(sku);
  const price = heldOverride === 0 ? 0 : negotiated;
  if (price == null) {
    return body;
  }
  const clone = JSON.parse(JSON.stringify(body)) as Record<string, unknown>;
  const sale = {
    type: 'SalePrice',
    value: price,
    currency: 'USD',
    currencyMnemonic: 'USD',
  };
  if (clone.salePrice) {
    clone.salePrice = { ...(clone.salePrice as object), ...sale };
  } else {
    clone.salePrice = sale;
  }
  if (Array.isArray(clone.prices)) {
    clone.prices = (clone.prices as Record<string, unknown>[]).map(p =>
      p?.type === 'SalePrice' ? { ...p, ...sale } : p
    );
  }
  // MVP 2.6.3 — warehouse held SKUs: $0 Your price only, cap qty at available owned stock
  if (heldOverride === 0) {
    delete clone.listPrice;
    if (Array.isArray(clone.prices)) {
      clone.prices = (clone.prices as Record<string, unknown>[]).filter(p => p?.type !== 'ListPrice');
    }
    const available = getPresidioWarehouseHeldItem(sku)?.availableQty;
    if (typeof available === 'number' && available >= 0) {
      clone.maxOrderQuantity = available;
      // Owned warehouse stock ignores catalog MOQ (was locking L40S at min 5 > max 4).
      clone.minOrderQuantity = available > 0 ? 1 : 0;
    }
  }
  return clone;
}

export function rejectSkuOutsideActiveCatalog(
  req: HttpRequest<unknown>,
  sku: string
): Observable<HttpEvent<unknown>> | null {
  if (!sku || isSkuAllowedForActiveCustomer(sku)) {
    return null;
  }
  return throwError(
    () =>
      new HttpErrorResponse({
        status: 403,
        error: {
          error: 'CATALOG_RESTRICTED',
          message: 'This product is not available for the active account catalog.',
        },
        url: req.url,
      })
  );
}

/** Prefer stub `sku` attribute; fall back to ICM link URI. Never use product title as SKU. */
export function extractProductListStubSku(el: {
  uri?: string;
  title?: string;
  attributes?: Array<{ name?: string; value?: unknown }>;
}): string {
  const fromAttr = el.attributes?.find(a => (a.name || '').toLowerCase() === 'sku')?.value;
  if (typeof fromAttr === 'string' && fromAttr.trim()) {
    return fromAttr.trim();
  }
  const uri = el.uri || '';
  const fromUri = uri.split('/products/')[1]?.split(/[/?]/)[0];
  if (fromUri) {
    try {
      return decodeURIComponent(fromUri);
    } catch {
      return fromUri;
    }
  }
  return '';
}

type ProductListStubElement = {
  title?: string;
  description?: string;
  attributes?: Array<{ name?: string; value?: unknown }>;
};

function stubAttr(el: ProductListStubElement, name: string): string | undefined {
  const value = el.attributes?.find(a => (a.name || '').toLowerCase() === name.toLowerCase())?.value;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * DEMO: product search matches title/keywords, manufacturer name, product family,
 * manufacturer SKU (catalog id === manufacturerSKU — there is no separate Presidio SKU),
 * and UNSPSC (including dotted forms such as 43.21.15.08).
 */
export function productStubMatchesSearchTerm(el: ProductListStubElement, searchTerm: string): boolean {
  const term = (searchTerm || '').trim().toLowerCase();
  if (!term) {
    return true;
  }
  const tokens = term.split(/\s+/).filter(Boolean);
  const manufacturerSku =
    stubAttr(el, 'manufacturerSKU') ?? stubAttr(el, 'ManufacturerSKU') ?? stubAttr(el, 'sku');
  const unspsc =
    stubAttr(el, 'UNSPSC') ?? stubAttr(el, 'unspsc') ?? lookupPresidioUnspsc(manufacturerSku);
  // Keep UNSPSC out of the free-text haystack so short tokens cannot substring-match codes.
  const haystack = [
    el.title,
    manufacturerSku,
    stubAttr(el, 'manufacturer') ?? stubAttr(el, 'Manufacturer'),
    stubAttr(el, 'ProductFamily') ?? stubAttr(el, 'productFamily'),
  ]
    .filter(v => v != null && v !== '')
    .map(String)
    .join(' ')
    .toLowerCase();
  return tokens.every(token => {
    if (haystack.includes(token)) {
      return true;
    }
    return unspscMatchesSearchToken(unspsc, token);
  });
}

/**
 * DEMO: rebuild Manufacturer / ProductFamily facets from the products that match
 * searchTerm (and account catalog), so SRP facets are not the static DGX fixture.
 */
export function buildProductFiltersFromProductStubs(
  products: { elements?: ProductListStubElement[] },
  options: {
    searchTerm?: string;
    selected?: Record<string, string[]>;
    /** ICM path for facet link URIs (default `productfilters`; WH PLP uses `categories/WH/productfilters`). */
    filterBasePath?: string;
    /** When true, do not narrow stubs by account catalog (already category-scoped lists). */
    skipCatalogFilter?: boolean;
  } = {}
): {
  elements: Array<{
    id: string;
    name: string;
    type: string;
    displayType: string;
    selectionType: string;
    limitCount: number;
    filterEntries: Array<{
      name: string;
      type: string;
      count: number;
      selected: boolean;
      level: number;
      displayValue: string;
      link: { type: string; uri: string; title: string };
    }>;
  }>;
  filterableAttributes: unknown[];
} {
  const searchTerm = (options.searchTerm || '').trim();
  const selected = options.selected || {};
  const filterBasePath = (options.filterBasePath || 'productfilters').replace(/^\/+|\/+$/g, '');
  const catalog = options.skipCatalogFilter ? undefined : getCatalogForActiveCustomer();
  const allowed = catalog ? new Set(catalog.allowedSkus) : null;

  let elements = Array.isArray(products?.elements) ? products.elements : [];
  if (allowed) {
    elements = elements.filter(el => {
      const sku = extractProductListStubSku(el);
      return !sku || allowed.has(sku);
    });
  }
  if (searchTerm) {
    elements = elements.filter(el => productStubMatchesSearchTerm(el, searchTerm));
  }

  const manufacturers = selected.Manufacturer || selected.manufacturer || [];
  const families = selected.ProductFamily || selected.productFamily || [];
  const inMyWarehouseSelected = isInMyWarehouseFilterSelected(selected);

  const manufacturerCounts = new Map<string, number>();
  const familyCounts = new Map<string, number>();
  let inMyWarehouseCount = 0;

  for (const el of elements) {
    const sku = extractProductListStubSku(el);
    const isHeld = isPresidioWarehouseHeldSku(sku);
    const manufacturer = stubAttr(el, 'manufacturer') ?? stubAttr(el, 'Manufacturer');
    const family = stubAttr(el, 'ProductFamily') ?? stubAttr(el, 'productFamily');
    const familyOk =
      !families.length ||
      (family &&
        families.some(f => family.toLowerCase() === f.toLowerCase() || family.toLowerCase().includes(f.toLowerCase())));
    const mfrOk =
      !manufacturers.length ||
      (manufacturer &&
        manufacturers.some(
          m => manufacturer.toLowerCase() === m.toLowerCase() || manufacturer.toLowerCase().includes(m.toLowerCase())
        ));
    const warehouseOk = !inMyWarehouseSelected || isHeld;

    // Facet counts: apply the other dimension's selection (standard faceted narrowing).
    if (manufacturer && familyOk && warehouseOk) {
      manufacturerCounts.set(manufacturer, (manufacturerCounts.get(manufacturer) || 0) + 1);
    }
    if (family && mfrOk && warehouseOk) {
      familyCounts.set(family, (familyCounts.get(family) || 0) + 1);
    }
    if (isHeld && mfrOk && familyOk) {
      inMyWarehouseCount += 1;
    }
  }

  const buildFilterLinkParams = (nextSelected: Record<string, string[]>) => {
    const params = new URLSearchParams();
    if (searchTerm) {
      params.set('searchTerm', searchTerm);
    }
    Object.entries(nextSelected).forEach(([key, values]) => {
      if (values?.length) {
        params.set(key, values.join(','));
      }
    });
    return params;
  };

  const toEntries = (filterKey: string, counts: Map<string, number>, selectedValues: string[]) =>
    [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => {
        const isSelected = selectedValues.some(
          v => v === name || decodeURIComponent(v) === name || v.toLowerCase() === name.toLowerCase()
        );
        const nextSelected = { ...selected };
        if (isSelected) {
          nextSelected[filterKey] = (nextSelected[filterKey] || []).filter(
            v => v !== name && decodeURIComponent(v) !== name
          );
          if (!nextSelected[filterKey]?.length) {
            delete nextSelected[filterKey];
          }
        } else {
          nextSelected[filterKey] = [...(nextSelected[filterKey] || []).filter(v => v !== name), name];
        }
        const query = buildFilterLinkParams(nextSelected).toString();
        return {
          name,
          type: 'Link',
          count,
          selected: isSelected,
          level: 0,
          displayValue: name,
          link: {
            type: 'Link',
            uri: `inSPIRED-inTRONICS_Business-Site/-/${filterBasePath}${query ? `?${query}` : ''}`,
            title: name,
          },
        };
      });

  const filters: Array<{
    id: string;
    name: string;
    type: string;
    displayType: string;
    selectionType: string;
    limitCount: number;
    filterEntries: Array<{
      name: string;
      type: string;
      count: number;
      selected: boolean;
      level: number;
      displayValue: string;
      link: { type: string; uri: string; title: string };
    }>;
  }> = [];

  // Warehouse accounts only — omit on My Warehouse category PLP (already held-stock).
  if (isPresidioMockWarehouseLogin() && !isMyWarehouseFilterBasePath(filterBasePath)) {
    const nextSelected = { ...selected };
    if (inMyWarehouseSelected) {
      delete nextSelected[PRESIDIO_IN_MY_WAREHOUSE_FILTER_ID];
      delete nextSelected['inmywarehouse'];
    } else {
      nextSelected[PRESIDIO_IN_MY_WAREHOUSE_FILTER_ID] = [PRESIDIO_IN_MY_WAREHOUSE_FILTER_VALUE];
    }
    const query = buildFilterLinkParams(nextSelected).toString();
    filters.push({
      id: PRESIDIO_IN_MY_WAREHOUSE_FILTER_ID,
      name: 'In My Warehouse',
      type: 'Filter',
      displayType: 'checkbox',
      selectionType: 'single',
      limitCount: -1,
      filterEntries: [
        {
          name: PRESIDIO_IN_MY_WAREHOUSE_FILTER_VALUE,
          type: 'Link',
          count: inMyWarehouseCount,
          selected: inMyWarehouseSelected,
          level: 0,
          displayValue: 'In My Warehouse',
          link: {
            type: 'Link',
            uri: `inSPIRED-inTRONICS_Business-Site/-/${filterBasePath}${query ? `?${query}` : ''}`,
            title: 'In My Warehouse',
          },
        },
      ],
    });
  }

  filters.push(
    {
      id: 'Manufacturer',
      name: 'Manufacturer',
      type: 'Filter',
      displayType: 'checkbox',
      selectionType: 'multiple',
      limitCount: -1,
      filterEntries: toEntries('Manufacturer', manufacturerCounts, manufacturers),
    },
    {
      id: 'ProductFamily',
      name: 'Product Family',
      type: 'Filter',
      displayType: 'checkbox',
      selectionType: 'multiple',
      limitCount: -1,
      filterEntries: toEntries('ProductFamily', familyCounts, families),
    }
  );

  return {
    elements: filters.filter(f => f.filterEntries.length > 0),
    filterableAttributes: [],
  };
}

export function filterProductListByActiveCatalog(body: unknown): unknown {
  const catalog = getCatalogForActiveCustomer();
  if (!body || typeof body !== 'object') {
    return body;
  }
  const clone = JSON.parse(JSON.stringify(body)) as {
    elements?: Array<{
      uri?: string;
      title?: string;
      attributes?: Array<{ name?: string; value?: unknown }>;
    }>;
    total?: number;
  };
  if (catalog && Array.isArray(clone.elements)) {
    const allowed = new Set(catalog.allowedSkus);
    clone.elements = clone.elements.filter(el => {
      const sku = extractProductListStubSku(el);
      // Keep unidentifiable rows; drop identified SKUs outside the account catalog.
      return !sku || allowed.has(sku);
    });
    // Keep total aligned when this runs on the full (pre-pagination) list.
    if (typeof clone.total === 'number') {
      clone.total = clone.elements.length;
    }
  }
  return applyWarehouseHeldPricesToProductList(clone);
}

/** MVP 2.6.3 — stamp $0 prices + qty bounds (min≤available, max=available) onto held SKUs in PLP stubs. */
function applyWarehouseHeldPricesToProductList(body: {
  elements?: Array<{ attributes?: Array<{ name?: string; value?: unknown }> }>;
}): unknown {
  if (!Array.isArray(body.elements) || !isPresidioMockWarehouseLogin()) {
    return body;
  }
  for (const el of body.elements) {
    const sku = extractProductListStubSku(el);
    if (!sku || warehouseHeldUnitPriceOverride(sku) !== 0) {
      continue;
    }
    const attrs = el.attributes;
    if (!Array.isArray(attrs)) {
      continue;
    }
    const available = getPresidioWarehouseHeldItem(sku)?.availableQty;
    el.attributes = attrs.filter(attr => attr.name !== 'listPrice');
    let hasMax = false;
    let hasMin = false;
    for (const attr of el.attributes) {
      if (attr.name === 'salePrice') {
        const money = attr.value as { value?: number; type?: string } | undefined;
        if (money && typeof money === 'object') {
          attr.value = { ...money, value: 0 };
        }
      }
      if (typeof available === 'number') {
        if (attr.name === 'maxOrderQuantity') {
          attr.value = available;
          hasMax = true;
        }
        if (attr.name === 'minOrderQuantity') {
          // Owned warehouse stock is not subject to catalog MOQ — allow 1..available.
          attr.value = available > 0 ? 1 : 0;
          hasMin = true;
        }
      }
    }
    if (typeof available === 'number') {
      if (!hasMax) {
        el.attributes.push({ name: 'maxOrderQuantity', value: available });
      }
      if (!hasMin) {
        el.attributes.push({ name: 'minOrderQuantity', value: 1 });
      }
    }
  }
  return body;
}

/**
 * MVP 2.6.3 — apply negotiated / warehouse-held unit prices onto productprices rows.
 * ICM shape uses prices.SalePrice[].gross|net.value (not row.salePrice).
 */
export function applyDiscoveryPricesToProductPricesPayload(body: unknown): unknown {
  if (!body || typeof body !== 'object') {
    return body;
  }
  const clone = JSON.parse(JSON.stringify(body)) as Record<string, unknown>;
  const rows = (clone.elements || clone.data) as Record<string, unknown>[] | undefined;
  if (!Array.isArray(rows)) {
    return clone;
  }
  for (const row of rows) {
    const sku = String(row.sku || row.product || '');
    if (!sku) {
      continue;
    }
    const held = warehouseHeldUnitPriceOverride(sku);
    const negotiated = held === 0 ? 0 : getNegotiatedPrice(sku);
    if (negotiated == null) {
      continue;
    }
    stampProductPriceRow(row, negotiated, held === 0);
  }
  return clone;
}

function stampProductPriceRow(row: Record<string, unknown>, value: number, warehouseHeld = false): void {
  const prices = row.prices as Record<string, unknown> | undefined;
  if (!prices || typeof prices !== 'object') {
    return;
  }
  // Warehouse held: Your/sale price only — drop list price from the contract
  const keys = warehouseHeld
    ? ['SalePrice', 'MinSalePrice', 'MaxSalePrice']
    : ['SalePrice', 'ListPrice', 'MinSalePrice', 'MinListPrice', 'MaxSalePrice', 'MaxListPrice'];
  if (warehouseHeld) {
    delete prices.ListPrice;
    delete prices.MinListPrice;
    delete prices.MaxListPrice;
    delete prices.SummedUpListPrice;
  }
  for (const key of keys) {
    const arr = prices[key];
    if (!Array.isArray(arr)) {
      continue;
    }
    for (const item of arr as Record<string, unknown>[]) {
      for (const side of ['gross', 'net']) {
        const money = item[side] as { value?: number } | undefined;
        if (money && typeof money === 'object') {
          item[side] = { ...money, value };
        }
      }
    }
  }
}

export interface DiscoveryPurchaseRuleIntent {
  login: string;
  reason: string;
}

export function isDiscoveryOrderCreatePath(method: string, restPath: string): boolean {
  const upper = method.toUpperCase();
  return (
    (upper === 'POST' && /baskets\/[^/]+\/purchases$/.test(restPath)) ||
    (upper === 'POST' && (restPath === 'orders' || restPath.startsWith('orders?'))) ||
    (upper === 'POST' && /customers\/[^/]+\/orders$/.test(restPath))
  );
}

/**
 * DEMO: evaluate purchase rules against current basket lines (call before cart clear).
 * Returns null when the request is not an order create, the cart holds quote lines
 * (FR10.10.2 / FR13.7.6), or no rule fires.
 */
export function evaluateDiscoveryPurchaseRuleIntent(
  method: string,
  restPath: string,
  readBasketLines: () => { sku: string; quantity: number; unitPrice: number; quoteId?: string }[]
): DiscoveryPurchaseRuleIntent | null {
  if (!isDiscoveryOrderCreatePath(method, restPath)) {
    return null;
  }
  const login = getPresidioMockActiveLogin();
  if (!login) {
    return null;
  }
  const lines = readBasketLines();
  // Quote carts ignore assigned purchase rules (quote Approve ≠ checkout purchase-rule approval).
  if (lines.some(l => !!l.quoteId)) {
    return null;
  }
  const subtotal = lines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
  const result = evaluatePurchaseRules({
    userLogin: login,
    subtotal,
    lines: lines.map(l => ({ sku: l.sku, unitPrice: l.unitPrice })),
  });
  if (!result.triggered) {
    return null;
  }
  return { login, reason: result.reason };
}

/** DEMO: active login contact for order confirmation / address stamping. */
export function getActiveMockBuyerContact(): {
  login: string;
  firstName: string;
  lastName: string;
  email: string;
} | null {
  const login = getPresidioMockActiveLogin();
  if (!login) {
    return null;
  }
  const discovery = getDiscoveryUser(login);
  if (discovery) {
    return {
      login,
      firstName: discovery.firstName || 'Demo',
      lastName: discovery.lastName || 'User',
      email: discovery.email || login,
    };
  }
  const target = PRESIDIO_MOCK_CSR_IMPERSONATION_TARGETS.find(t => t.login.toLowerCase() === login.toLowerCase());
  if (target) {
    return {
      login,
      firstName: target.firstName,
      lastName: target.lastName,
      email: login,
    };
  }
  const local = login.split('@')[0] || 'demo';
  const parts = local.split(/[._-]+/).filter(Boolean);
  const title = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
  return {
    login,
    firstName: title(parts[0] || 'Demo'),
    lastName: title(parts[1] || 'User'),
    email: login,
  };
}

/** Stamp first/last/email onto a mock address object for the active buyer. */
export function applyActiveBuyerContactToAddress(address: Record<string, unknown>): Record<string, unknown> {
  const buyer = getActiveMockBuyerContact();
  if (!buyer) {
    return address;
  }
  return {
    ...address,
    firstName: buyer.firstName,
    lastName: buyer.lastName,
    email: buyer.email,
  };
}

/**
 * DEMO: ICM order create fixtures hard-code Jordan Hale as buyer/addresses.
 * Stamp the active mock login so confirmation matches the signed-in user.
 */
export function stampActiveBuyerOntoOrderPayload(body: unknown): unknown {
  const buyer = getActiveMockBuyerContact();
  if (!buyer || !body || typeof body !== 'object') {
    return body;
  }
  const clone = JSON.parse(JSON.stringify(body)) as {
    data?: Record<string, unknown>;
    included?: Record<string, unknown>;
  };
  const data = clone.data ?? (clone as Record<string, unknown>);
  data.buyer = {
    ...(typeof data.buyer === 'object' && data.buyer ? (data.buyer as Record<string, unknown>) : {}),
    firstName: buyer.firstName,
    lastName: buyer.lastName,
  };
  // OrderMapper maps `data.user` → order.email
  data.user = buyer.email;
  data.email = buyer.email;

  const stampIncludedBucket = (bucket: unknown) => {
    if (!bucket || typeof bucket !== 'object') {
      return;
    }
    for (const key of Object.keys(bucket as Record<string, unknown>)) {
      const addr = (bucket as Record<string, unknown>)[key];
      if (addr && typeof addr === 'object') {
        (bucket as Record<string, unknown>)[key] = applyActiveBuyerContactToAddress(addr as Record<string, unknown>);
      }
    }
  };

  if (clone.included) {
    stampIncludedBucket(clone.included.invoiceToAddress);
    stampIncludedBucket(clone.included.commonShipToAddress);
    stampIncludedBucket(clone.included.buckets_shipToAddress);
  }

  if (clone.data) {
    clone.data = data;
    return clone;
  }
  return data;
}

/** Stamp a valid ICM order payload as Pending Approval (mapper-safe). */
export function applyDiscoveryPendingApprovalToOrderPayload(
  body: unknown,
  intent: DiscoveryPurchaseRuleIntent
): unknown {
  if (!body || typeof body !== 'object') {
    return body;
  }
  const clone = JSON.parse(JSON.stringify(body)) as { data?: Record<string, unknown> };
  const data = clone.data ?? (clone as Record<string, unknown>);
  data.status = 'Pending Approval';
  data.statusCode = 'PENDING';
  data.pendingApproval = true;
  data.approvalReason = intent.reason;
  // Surfaced on Approvals detail as the purchase-rule reason (Presidio summary).
  data.messageToMerchant = intent.reason;

  const totals = data.totals as
    | { grandTotal?: { gross?: { value?: number; currency?: string }; net?: { value?: number } } }
    | undefined;
  const lineItems = Array.isArray(data.lineItems) ? data.lineItems : [];
  const totalGross =
    totals?.grandTotal?.gross?.value ??
    (typeof data.totalGross === 'object' && data.totalGross && 'value' in (data.totalGross as object)
      ? Number((data.totalGross as { value?: number }).value)
      : 0);
  const totalNet =
    totals?.grandTotal?.net?.value ??
    (typeof data.totalNet === 'object' && data.totalNet && 'value' in (data.totalNet as object)
      ? Number((data.totalNet as { value?: number }).value)
      : totalGross);
  const currency = totals?.grandTotal?.gross?.currency || 'USD';
  const orderId = String(data.id || `PA-${Date.now()}`);
  // Shopper-facing request # (confirmation page / email) — distinct from future order #.
  // Maps to Order.requisitionNo via OrderMapper (`requisitionDocumentNo`).
  const requestNo = String(
    data.requisitionDocumentNo || data.requisitionNo || `PA-${String(Date.now()).slice(-6)}`
  );
  data.requisitionDocumentNo = requestNo;
  // Pending approval is a request, not a placed order — omit order document # until approved.
  delete data.documentNo;
  delete data.documentNumber;

  recordPendingApprovalOrder(intent.login, intent.reason, {
    orderId,
    requisitionNo: requestNo,
    totalGross: Number(totalGross) || 0,
    totalNet: Number(totalNet) || 0,
    currency,
    lineItemCount: lineItems.length,
    detailPayload: clone.data ? clone : { data },
  });

  if (clone.data) {
    clone.data = data;
    return clone;
  }
  return data;
}

/**
 * DEMO MVP 1.2.4 / 1.2.6: DIAS requisition routes backed by purchase-rule pending orders.
 * Returns null when the path is not a requisitions API call.
 */
export function handlePresidioPurchaseApprovals(
  req: HttpRequest<unknown>,
  restPath: string,
  readJsonBody: (body: unknown) => unknown
): Observable<HttpEvent<unknown>> | null {
  const decoded = (() => {
    try {
      return decodeURIComponent(restPath);
    } catch {
      return restPath;
    }
  })();

  const listMatch = /^customers\/[^/]+\/users\/[^/]+\/requisitions$/i.test(decoded);
  const itemMatch = /^customers\/[^/]+\/users\/[^/]+\/requisitions\/([^/]+)$/i.exec(decoded);
  if (!listMatch && !itemMatch) {
    return null;
  }

  const method = req.method.toUpperCase();
  const queryView =
    req.params?.get('view') || new URL(req.url, 'http://local').searchParams.get('view') || 'buyer';
  const queryStatus =
    req.params?.get('status') || new URL(req.url, 'http://local').searchParams.get('status') || 'PENDING';

  if (listMatch && method === 'GET') {
    const view = (queryView === 'approver' ? 'approver' : 'buyer') as 'buyer' | 'approver';
    const statusParam = queryStatus.toUpperCase() as PresidioDiscoveryPendingApprovalStatus;
    const status: PresidioDiscoveryPendingApprovalStatus =
      statusParam === 'APPROVED' || statusParam === 'REJECTED' || statusParam === 'PENDING'
        ? statusParam
        : 'PENDING';
    const rows = listPendingApprovalOrdersForView(view, status).map(toRequisitionListRow);
    return of(new HttpResponse({ status: 200, body: { data: rows } }));
  }

  if (itemMatch && method === 'GET') {
    const id = decodeURIComponent(itemMatch[1]);
    const order = getPendingApprovalOrder(id);
    if (!order) {
      return throwError(
        () => new HttpErrorResponse({ status: 404, error: { error: 'Approval request not found' }, url: req.url })
      );
    }
    return of(new HttpResponse({ status: 200, body: buildRequisitionDetailPayload(order) }));
  }

  if (itemMatch && method === 'PATCH') {
    const id = decodeURIComponent(itemMatch[1]);
    const body = readJsonBody(req.body) as { statusCode?: string; approvalComment?: string } | null;
    const statusCode = (body?.statusCode || '').toUpperCase() as PresidioDiscoveryPendingApprovalStatus;
    if (statusCode !== 'APPROVED' && statusCode !== 'REJECTED') {
      return throwError(
        () => new HttpErrorResponse({ status: 400, error: { error: 'statusCode required' }, url: req.url })
      );
    }
    const updated = updatePendingApprovalOrderStatus(id, statusCode, body?.approvalComment);
    if (!updated) {
      return throwError(
        () => new HttpErrorResponse({ status: 404, error: { error: 'Approval request not found' }, url: req.url })
      );
    }
    return of(new HttpResponse({ status: 200, body: buildRequisitionDetailPayload(updated) }));
  }

  return null;
}

function toRequisitionListRow(order: PresidioDiscoveryPendingApprovalOrder): Record<string, unknown> {
  const buyer = getPresidioDiscoveryState().users.find(
    u => u.login.toLowerCase() === order.userLogin.toLowerCase()
  );
  const approver = order.approverLogin
    ? getPresidioDiscoveryState().users.find(u => u.login.toLowerCase() === order.approverLogin!.toLowerCase())
    : undefined;
  return {
    id: order.id,
    requisitionNo: order.requisitionNo,
    orderNo: order.orderNo,
    creationDate: Date.parse(order.createdAt) || Date.now(),
    lineItemCount: order.lineItemCount,
    purchaseCurrency: order.currency,
    messageToMerchant: order.reason,
    totalGross: { currency: order.currency, value: order.totalGross },
    totalNet: { currency: order.currency, value: order.totalNet },
    userInformation: {
      firstName: buyer?.firstName || 'Buyer',
      lastName: buyer?.lastName || '',
      email: order.userLogin,
    },
    approvalStatus: {
      status: order.status.toLowerCase(),
      statusCode: order.status,
      approvalDate: order.approvalDate,
      approvalComment: order.approvalComment,
    },
    approvalStatuses: approver
      ? [
          {
            statusCode: order.status === 'PENDING' ? 'PENDING' : order.status,
            approvalDate: order.approvalDate,
            approver: {
              email: approver.login,
              firstName: approver.firstName,
              lastName: approver.lastName,
            },
          },
        ]
      : undefined,
    approval: {
      customerApproval: {
        approvers: approver
          ? [{ email: approver.login, firstName: approver.firstName, lastName: approver.lastName }]
          : [],
      },
    },
  };
}

function buildRequisitionDetailPayload(order: PresidioDiscoveryPendingApprovalOrder): Record<string, unknown> {
  if (order.detailPayload && typeof order.detailPayload === 'object') {
    const clone = JSON.parse(JSON.stringify(order.detailPayload)) as {
      data?: Record<string, unknown>;
      included?: Record<string, unknown>;
    };
    const data = clone.data || {};
    data.id = order.id;
    data.requisitionNo = order.requisitionNo;
    data.orderNo = order.orderNo;
    const purchaseRuleReason =
      order.reason ||
      (typeof data.approvalReason === 'string' && data.approvalReason) ||
      (typeof data.messageToMerchant === 'string' && data.messageToMerchant) ||
      '';
    data.messageToMerchant = purchaseRuleReason;
    data.approvalReason = purchaseRuleReason;
    data.approvalStatus = {
      status: order.status.toLowerCase(),
      statusCode: order.status,
      approvalDate: order.approvalDate,
      approvalComment: order.approvalComment,
    };
    // Prefer list-row purchase-rule reason over any stale basket messageToMerchant
    clone.data = {
      ...data,
      ...toRequisitionListRow(order),
      messageToMerchant: purchaseRuleReason || order.reason,
      approvalReason: purchaseRuleReason || order.reason,
    };
    return clone;
  }

  const listRow = toRequisitionListRow(order);
  const sku = order.reason.includes('NV-DGX-H100')
    ? 'NV-DGX-H100'
    : order.reason.includes('PRS-CISCO')
      ? 'CISCO-C9300'
      : 'C9200-48P';
  const lineId = `li-${order.id}-1`;
  const unit = order.lineItemCount > 0 ? order.totalGross / Math.max(order.lineItemCount, 1) : order.totalGross;

  return {
    data: {
      ...listRow,
      calculated: true,
      customer: 'PRESIDIO-MOCK',
      invoiceToAddress: 'urn_addr_inv',
      commonShipToAddress: 'urn_addr_ship',
      commonShippingMethod: 'STD_GROUND',
      payments: ['open-tender'],
      lineItems: [lineId],
      totals: {
        itemTotal: {
          gross: { currency: order.currency, value: order.totalGross },
          net: { currency: order.currency, value: order.totalNet },
        },
        grandTotal: {
          gross: { currency: order.currency, value: order.totalGross },
          net: { currency: order.currency, value: order.totalNet },
        },
      },
      messageToMerchant: order.reason,
    },
    included: {
      lineItems: {
        [lineId]: {
          id: lineId,
          calculated: true,
          position: 1,
          freeGift: false,
          hiddenGift: false,
          quantity: { value: 1, unit: 'pcs' },
          product: sku,
          pricing: {
            singleBasePrice: {
              gross: { value: unit, currency: order.currency },
              net: { value: unit, currency: order.currency },
            },
            price: {
              gross: { value: unit, currency: order.currency },
              net: { value: unit, currency: order.currency },
            },
            total: {
              gross: { value: unit, currency: order.currency },
              net: { value: unit, currency: order.currency },
            },
          },
        },
      },
      invoiceToAddress: {
        urn_addr_inv: {
          id: 'addr-inv',
          urn: 'urn_addr_inv',
          type: 'Address',
          companyName1: 'Acme Healthcare',
          firstName: listRow['userInformation']
            ? (listRow['userInformation'] as { firstName?: string }).firstName
            : 'Alex',
          lastName: listRow['userInformation']
            ? (listRow['userInformation'] as { lastName?: string }).lastName
            : 'Rivera',
          addressLine1: '100 Healthcare Way',
          postalCode: '78701',
          city: 'Austin',
          mainDivision: 'TX',
          mainDivisionCode: 'TX',
          country: 'United States',
          countryCode: 'US',
          email: order.userLogin,
          invoiceToAddress: true,
          shipToAddress: true,
          street: '100 Healthcare Way',
        },
      },
      commonShipToAddress: {
        urn_addr_ship: {
          id: 'addr-ship',
          urn: 'urn_addr_ship',
          type: 'Address',
          companyName1: 'Acme Healthcare',
          firstName: listRow['userInformation']
            ? (listRow['userInformation'] as { firstName?: string }).firstName
            : 'Alex',
          lastName: listRow['userInformation']
            ? (listRow['userInformation'] as { lastName?: string }).lastName
            : 'Rivera',
          addressLine1: '500 Server Farm Blvd',
          postalCode: '78758',
          city: 'Austin',
          mainDivision: 'TX',
          mainDivisionCode: 'TX',
          country: 'United States',
          countryCode: 'US',
          email: order.userLogin,
          invoiceToAddress: false,
          shipToAddress: true,
          street: '500 Server Farm Blvd',
        },
      },
      payments: {
        'open-tender': {
          id: 'open-tender',
          paymentInstrument: 'PRS_CC_SAVED_1111',
          paymentMethod: 'ISH_CreditCard',
          redirectRequired: false,
          status: 'Unprocessed',
        },
      },
      payments_paymentMethod: {
        ISH_CreditCard: {
          id: 'ISH_CreditCard',
          serviceID: 'ISH_CreditCard',
          displayName: 'Credit Card',
          paymentInstruments: ['PRS_CC_SAVED_1111'],
        },
      },
      payments_paymentInstrument: {
        PRS_CC_SAVED_1111: {
          id: 'PRS_CC_SAVED_1111',
          accountIdentifier: 'Visa ···· 1111',
        },
      },
      commonShippingMethod: {
        STD_GROUND: {
          id: 'STD_GROUND',
          name: 'Standard Ground',
          description: '5-7 business days',
        },
      },
    },
  };
}

/** Overlay Pending Approval onto the confirmation row in order lists. */
export function applyDiscoveryPendingOrdersToList(restPath: string, method: string, body: unknown): unknown {
  if (method.toUpperCase() !== 'GET' || !body || typeof body !== 'object') {
    return body;
  }
  const isList = restPath === 'custom-orders' || restPath === 'orders' || restPath.startsWith('custom-orders?');
  if (!isList) {
    return body;
  }
  const pending = getPresidioDiscoveryState().pendingApprovalOrders[0];
  if (!pending) {
    return body;
  }
  const clone = JSON.parse(JSON.stringify(body)) as { data?: Record<string, unknown>[] };
  if (!Array.isArray(clone.data)) {
    return body;
  }
  const confirmation = clone.data.find(row => row.id === 'MOCK-ORD-NEW');
  if (confirmation) {
    confirmation.status = 'Pending Approval';
    confirmation.statusCode = 'PENDING';
    confirmation.pendingApproval = true;
    confirmation.presidioTitle = confirmation.presidioTitle || 'Pending approval';
  }
  return clone;
}

/** DEMO: emails that already have a MyPresidio login / active discovery user. */
const PRESIDIO_MOCK_REGISTERED_EMAILS = [
  'jordan.hale@acme.com',
  'alex.rivera@acme.com',
  'jamie.stone@acme.com',
  'morgan.chen@acme.com',
  'sam.patel@acme.com',
  'riley.sales@presidio.com',
  'quinn.csr@presidio.com',
  'casey.ware@presidio-wh.com',
  'pat.punch@acme.com',
] as const;

/**
 * DEMO: true when the email already belongs to a mock login or discovery user.
 * Used to block Request access and prompt password reset / admin help.
 */
export function isPresidioMockEmailAlreadyRegistered(email: string): boolean {
  const key = (email || '').trim().toLowerCase();
  if (!key) {
    return false;
  }
  if (PRESIDIO_MOCK_REGISTERED_EMAILS.some(e => e === key)) {
    return true;
  }
  return getPresidioDiscoveryState().users.some(
    u => u.login.toLowerCase() === key || u.email.toLowerCase() === key
  );
}

export function recordDiscoveryAccessRequest(input: {
  email: string;
  firstName?: string;
  lastName?: string;
  companyName: string;
  requestedRole: 'standard' | 'administrator';
  hasAdministrators: boolean;
  hasAssignedCsr?: boolean;
  salesRepName?: string;
  department?: string;
  title?: string;
  phone?: string;
}): { route: 'admin' | 'sales'; reviewerLabel: string } {
  const route = input.hasAdministrators ? 'admin' : 'sales';
  const reviewerLabel = input.hasAdministrators
    ? 'Account administrators'
    : input.salesRepName || 'Assigned Presidio sales representative';
  addAccessRequest({
    email: input.email,
    firstName: input.firstName,
    lastName: input.lastName,
    companyName: input.companyName,
    requestedRole: input.requestedRole,
    routeTo: route === 'admin' ? 'administrators' : 'sales_rep',
    reviewerLabel,
    department: input.department,
    title: input.title,
    phone: input.phone,
  });
  return { route, reviewerLabel };
}

/** Mock Microsoft SSO success — no production Azure verification. */
export function buildMockMicrosoftSsoTokenResponse(login = 'jordan.hale@acme.com'): Record<string, unknown> {
  return {
    access_token: 'presidio-mock-ms-sso-token',
    token_type: 'Bearer',
    expires_in: 3600,
    refresh_token: 'presidio-mock-ms-sso-refresh',
    scope: 'openid',
    id_token: 'presidio-mock-ms-id-token',
    demo_sso: true,
    demo_provider: 'Microsoft',
    demo_login: login,
  };
}
